<!-- Adapted from YesPlayMusic v0.4.11 artist.vue (MIT). Artist sections use the shared catalogue and playback runtime. -->
<template>
  <div class="artist-page" :class="{ 'salt-detail': salt }">
    <div v-if="loading" class="page-state" role="status">正在载入艺术家…</div>
    <div v-else-if="error" class="page-state" role="alert"><p>{{ error }}</p><SaltButton v-if="salt" data-scroll-retry @click="loadData" style-type="subtle">重试</SaltButton><ButtonTwoTone v-else color="grey" data-scroll-retry @click.native="loadData">重试</ButtonTwoTone></div>
    <template v-else>
      <div class="artist-info">
        <div class="head"><img :src="coverUrl" :alt="artist.name" loading="lazy" /></div>
        <div>
          <div class="name">{{ artist.name }}</div><div class="artist">艺术家</div>
          <div class="statistics"><a href="#artist-songs" @click.prevent="setSection('songs')">{{ songSummary }}</a> · <a href="#artist-albums" @click.prevent="setSection('albums')">{{ albumSummary }}</a></div>
          <div v-if="artist.briefDesc || artist.description" class="description" :class="{ expanded: fullDescription }" tabindex="0" @click="fullDescription = !fullDescription" @keydown.enter="fullDescription = !fullDescription">{{ artist.briefDesc || artist.description }}</div>
          <div class="buttons detail-actions">
            <SaltButton v-if="salt" :disabled="songActionsDisabled" @click="playAll"><template #leading><svg-icon icon-class="play" /></template><span class="detail-action-label">{{ collecting ? '正在收集歌曲…' : '播放' }}</span><span class="detail-busy-label">{{ collecting ? '加载中' : '播放' }}</span></SaltButton>
            <ButtonTwoTone v-else icon-class="play" :disabled="songActionsDisabled" @click.native="playAll"><span class="detail-action-label">{{ collecting ? '正在收集歌曲…' : '播放' }}</span><span class="detail-busy-label">{{ collecting ? '加载中' : '播放' }}</span></ButtonTwoTone>
            <SaltButton v-if="salt" :disabled="songActionsDisabled" @click="saveArtist" style-type="subtle" class="detail-secondary" aria-label="保存歌曲为歌单" title="保存歌曲为歌单"><svg-icon class="detail-action-icon" icon-class="plus" aria-hidden="true" /><span class="detail-action-label">保存歌曲为歌单</span></SaltButton>
            <ButtonTwoTone v-else color="grey" :disabled="songActionsDisabled" @click.native="saveArtist" class="detail-secondary" aria-label="保存歌曲为歌单" title="保存歌曲为歌单"><svg-icon class="detail-action-icon" icon-class="plus" aria-hidden="true" /><span class="detail-action-label">保存歌曲为歌单</span></ButtonTwoTone>
          </div>
          <div v-if="!isLocal && (songsIncomplete || (activeSection !== 'songs' && pages.songs.error))" class="page-state artist-song-retry" role="alert">
            {{ pages.songs.error || '歌曲尚未完整读取，请重试后再播放或保存全部。' }}
            <SaltButton v-if="salt" style-type="plain" data-scroll-retry :disabled="collecting || pages.songs.loading" @click="retrySongs">重试歌曲</SaltButton><button v-else type="button" data-scroll-retry :disabled="collecting || pages.songs.loading" @click="retrySongs">重试歌曲</button>
          </div>
        </div>
      </div>
      <div class="artist-sections" role="tablist" aria-label="艺术家作品">
        <template v-for="section in sections">
          <SaltButton v-if="salt" :key="section.id" role="tab" :id="'artist-tab-' + section.id" :aria-selected="activeSection === section.id ? 'true' : 'false'" :aria-controls="'artist-' + section.id" :class="{ selected: activeSection === section.id }" style-type="plain" @click="setSection(section.id)">{{ section.name }}</SaltButton>
          <button v-else :key="section.id" type="button" role="tab" :id="'artist-tab-' + section.id" :aria-selected="activeSection === section.id ? 'true' : 'false'" :aria-controls="'artist-' + section.id" :class="{ selected: activeSection === section.id }" @click="setSection(section.id)">{{ section.name }}</button>
        </template>
      </div>
      <section v-if="activeSection === 'songs'" id="artist-songs" class="popular-tracks" role="tabpanel" aria-labelledby="artist-tab-songs">
        <div class="section-title">{{ isLocal ? '音乐库中的歌曲' : pages.songs.scope === 'popular' ? '热门歌曲' : '歌曲' }}<span v-if="!isLocal" class="loaded-count">已加载 {{ tracks.length }}<template v-if="pages.songs.total !== null"> / {{ pages.songs.total }}</template> 首</span></div>
        <!-- Clicking one visible song should start it immediately. Full artist
             pagination remains reserved for the explicit Play/Save actions. -->
        <TrackList v-if="tracks.length" :tracks="tracks" preserve-playback-context type="tracklist" />
        <p v-else-if="isLocal || (!currentPage.loading && !currentPage.error)" class="page-state">暂无可读取的歌曲。</p>
      </section>
      <section v-else id="artist-albums" class="albums" role="tabpanel" aria-labelledby="artist-tab-albums">
        <div class="section-title">专辑<span v-if="!isLocal" class="loaded-count">已加载 {{ albums.length }}<template v-if="pages.albums.total !== null"> / {{ pages.albums.total }}</template> 张</span></div>
        <CoverRow v-if="albums.length" :style="salt ? { gridTemplateColumns: 'repeat(auto-fill, minmax(128px, 1fr))' } : null" :sub-text-font-size="salt ? '14px' : '16px'" :gap="salt ? '16px' : '44px 24px'" type="album" :source-filter="isLocal ? localSourceFilter : 'all'" :items="albums" sub-text="releaseYear" :show-play-button="true" />
        <p v-else-if="isLocal || (!currentPage.loading && !currentPage.error)" class="page-state">暂无可读取的专辑。</p>
      </section>
      <div v-if="!isLocal" ref="prefetchMarker" class="artist-page-tail" aria-live="polite">
        <p v-if="currentPage.loading" class="page-state" role="status">正在载入{{ activeSection === 'songs' ? '歌曲' : '专辑' }}…</p>
        <div v-else-if="currentPage.error" class="page-state" role="alert">{{ currentPage.error }} <SaltButton v-if="salt" style-type="plain" data-scroll-retry @click="retrySection">重试</SaltButton><button v-else type="button" data-scroll-retry @click="retrySection">重试</button></div>
        <div v-else-if="activeSection === 'albums' && albumsIncomplete" class="page-state artist-album-retry" role="alert">专辑尚未完整读取。
          <SaltButton v-if="salt" style-type="plain" data-scroll-retry @click="retryAlbums">重试专辑</SaltButton><button v-else type="button" data-scroll-retry @click="retryAlbums">重试专辑</button>
        </div>
        <p v-else-if="currentPage.loaded && currentPage.nextCursor === null && !(activeSection === 'songs' ? songsIncomplete : albumsIncomplete) && (activeSection === 'songs' ? tracks.length : albums.length)" class="page-state">{{ currentPage.scope === 'popular' ? '已显示可读取的热门歌曲' : '已显示全部可读取作品' }}</p>
      </div>
    </template>
  </div>
</template>
<script>
import { runtime } from '../bridge';
import { workspace } from '../workspace';
import { albumGroupKey, albumRouteId } from '../album-identity';
import { matchesLibrarySource, normalizeLibrarySource } from '../library-source';
import { observeListPrefetch } from '../list-prefetch';
import { readWorkspaceArtistSection, rememberWorkspaceArtistSection } from '../workspace-view-state';
import ButtonTwoTone from '../components/ButtonTwoTone.vue';
import SaltButton from '../salt/ui/SaltButton.vue';
import TrackList from '../components/TrackList.vue';
import CoverRow from '../components/CoverRow.vue';

const sectionState = () => ({ items: [], total: null, nextCursor: null, loaded: false, loading: false, error: '', scope: 'all', partial: false, cursors: [], token: 0, pending: null });
export default {
  name: 'Artist',
  components: { ButtonTwoTone, SaltButton, TrackList, CoverRow },
  data() { return { workspace, state: runtime.state, remoteItem: {}, pages: { songs: sectionState(), albums: sectionState() }, sections: [{ id: 'songs', name: '歌曲' }, { id: 'albums', name: '专辑' }], activeSection: 'songs', loading: false, error: '', fullDescription: false, requestId: 0, collecting: false, sectionScroll: { songs: 0, albums: 0 } }; },
  computed: {
    salt() { return this.workspace.appearance === 'salt'; },
    routeId() { return String(this.$route.params.id || ''); },
    routeSection() { const value = this.$route.query.section; return value === 'songs' || value === 'albums' ? value : null; },
    isLocal() { return this.routeId.startsWith('local:'); },
    localName() { try { return decodeURIComponent(this.routeId.slice(6)); } catch { return this.routeId.slice(6); } },
    localSourceFilter() { return normalizeLibrarySource(this.$route.query.source); },
    tracks() { return this.isLocal ? runtime.libraryTracks().filter(t => t.artists.includes(this.localName) && matchesLibrarySource(t, this.localSourceFilter)) : this.pages.songs.items; },
    songActionsDisabled() { return this.collecting || (!this.tracks.length && (this.isLocal || (this.pages.songs.loaded && this.pages.songs.nextCursor === null))); },
    songsIncomplete() { const page = this.pages.songs; return page.loaded && (page.partial || page.scope !== 'all' || (page.nextCursor === null && page.total !== null && page.items.length < page.total)); },
    albumsIncomplete() { const page = this.pages.albums; return page.loaded && page.nextCursor === null && (page.partial || page.scope !== 'all' || (page.total !== null && page.items.length < page.total)); },
    artist() { return this.isLocal ? { id: this.routeId, name: this.localName } : this.remoteItem; },
    coverUrl() { return this.artist.img1v1Url || this.artist.picUrl || runtime.cover(this.tracks[0]); },
    albums() { if (!this.isLocal) return this.pages.albums.items; const groups = new Map(); this.tracks.forEach(track => { const key = albumGroupKey(track); if (!groups.has(key)) groups.set(key, { id: albumRouteId(track), name: track.album || '未知专辑', picUrl: runtime.cover(track), size: 0 }); groups.get(key).size++; }); return Array.from(groups.values()); },
    currentPage() { return this.pages[this.activeSection]; },
    scrollRestoreState() { return { busy: this.loading || this.currentPage.loading, more: !this.isLocal && (!this.currentPage.loaded || this.currentPage.nextCursor !== null), failed: Boolean(this.error || this.currentPage.error) }; },
    songSummary() { return this.isLocal ? this.tracks.length + ' 首歌曲' : this.pages.songs.total !== null ? this.pages.songs.total + ' 首' + (this.pages.songs.scope === 'popular' ? '热门歌曲' : '歌曲') : this.tracks.length + ' 首已加载歌曲'; },
    albumSummary() { const total = this.pages.albums.total; return this.isLocal ? this.albums.length + ' 张专辑' : total !== null ? total + ' 张专辑' : this.albums.length + ' 张已加载专辑'; },
  },
  watch: { routeId: { immediate: true, handler() { this.loadData(); } }, routeSection(section) { if (section) this.setSection(section); } },
  mounted() { this.bindPrefetch(); },
  beforeDestroy() { this.requestId++; this.disconnectPrefetch(); },
  methods: {
    disconnectPrefetch() { if (this.disposePrefetch) this.disposePrefetch(); this.disposePrefetch = null; },
    bindPrefetch() {
      this.disconnectPrefetch();
      if (this.isLocal || this.loading || !this.$refs.prefetchMarker) return;
      this.disposePrefetch = observeListPrefetch(this.$refs.prefetchMarker, () => {
        const page = this.currentPage;
        if (!this.collecting && !page.loading && !page.error && (!page.loaded || page.nextCursor !== null)) this.loadPage(this.activeSection);
      }, this.activeSection === 'songs' ? 30 : 3);
    },
    async loadData() {
      const ticket = ++this.requestId;
      this.disconnectPrefetch(); this.error = ''; this.collecting = false; this._collectPromise = null; this.fullDescription = false; this.activeSection = this.routeSection || (this.salt ? readWorkspaceArtistSection(this.workspace.mode, this.$route.fullPath) : 'songs'); this.remoteItem = {};
      this.pages = { songs: sectionState(), albums: sectionState() }; this.sectionScroll = { songs: 0, albums: 0 };
      if (this.isLocal) { this.loading = false; return; }
      this.loading = true;
      const ok = await this.loadPage(this.activeSection);
      if (ticket !== this.requestId) return;
      this.loading = false;
      if (!ok) this.error = this.currentPage.error || '艺术家载入失败';
      this.$nextTick(this.bindPrefetch);
    },
    setSection(section) {
      if (section === this.activeSection) return;
      if (this.salt) rememberWorkspaceArtistSection(this.workspace.mode, this.$route.fullPath, section);
      const main = this.$el.closest('main');
      this.sectionScroll[this.activeSection] = main?.scrollTop || 0;
      const previous = this.currentPage;
      previous.token++; previous.loading = false; previous.pending = null;
      if (previous === this.pages.songs) { this.collecting = false; this._collectPromise = null; }
      this.disconnectPrefetch(); this.activeSection = section;
      this.$nextTick(() => {
        if (main) main.scrollTop = this.sectionScroll[section];
        if (!this.isLocal && !this.currentPage.loaded) this.loadPage(section);
        this.bindPrefetch();
      });
    },
    retrySection() {
      if (this.activeSection === 'songs' && this.songsIncomplete) return this.retrySongs();
      if (this.activeSection === 'albums' && this.albumsIncomplete) return this.retryAlbums();
      return this.loadPage(this.activeSection);
    },
    retrySongs() { return this.collectSongs(this.songsIncomplete).catch(() => []); },
    retryAlbums() {
      const owner = this.pages.albums;
      if (this.isLocal || this.activeSection !== 'albums') return Promise.resolve(false);
      if (owner.loading) return owner.pending;
      const ticket = this.requestId, token = ++owner.token, page = sectionState();
      const current = () => ticket === this.requestId && this.pages.albums === owner && owner.token === token && this.activeSection === 'albums' && owner.pending === operation;
      owner.loading = true; owner.error = '';
      const operation = (async () => {
        do {
          const ok = await this.loadPage('albums', false, page);
          if (!current()) return false;
          if (!ok) throw new Error(page.error || '专辑重读失败');
        } while (page.nextCursor !== null);
        if (page.scope !== 'all' || page.partial || (page.total !== null && page.items.length < page.total)) throw new Error('部分专辑暂时无法读取，请重试');
        // Keep the displayed list intact until the whole replacement succeeds.
        for (const key of ['items', 'total', 'nextCursor', 'loaded', 'scope', 'partial', 'cursors']) owner[key] = page[key];
        return true;
      })().catch(error => { if (current()) owner.error = error.message || '专辑重读失败'; return false; }).finally(() => {
        if (current()) { owner.loading = false; owner.pending = null; this.bindPrefetch(); }
      });
      owner.pending = operation;
      return operation;
    },
    loadPage(section, allowInactive = false, replacement = null) {
      const owner = this.pages[section], page = replacement || owner;
      if (page.loading) return page.pending;
      if (this.isLocal || (page.loaded && page.nextCursor === null)) return Promise.resolve(true);
      const offset = page.loaded ? page.nextCursor : '0';
      if (page.cursors.includes(offset)) { page.error = '作品分页未向前推进，请重试'; return Promise.resolve(false); }
      const ticket = this.requestId, token = ++page.token, ownerToken = owner.token, routeId = this.routeId;
      const current = () => ticket === this.requestId && this.pages[section] === owner && ownerToken === owner.token && token === page.token;
      page.loading = true; page.error = '';
      page.pending = (async () => {
        try {
          const data = await runtime.catalog('artist', routeId, { section, offset, limit: 50 });
          if (!current() || (!allowInactive && section !== this.activeSection)) return false;
          if (!data.item) throw new Error('没有找到这位艺术家');
          const next = data.more ? String(data.nextCursor ?? '') : null;
          if (next !== null && (!/^\d+$/.test(next) || !Number.isSafeInteger(Number(next)) || Number(next) <= Number(offset))) throw new Error('作品分页未向前推进，请重试');
          const known = new Set(page.items.map(item => item.id));
          const incoming = section === 'songs' ? data.tracks || [] : data.albums || [];
          if (page.loaded && next !== null && !incoming.some(item => item.id && !known.has(item.id))) throw new Error('作品分页未向前推进，请重试');
          for (const item of incoming) if (item.id && !known.has(item.id)) { known.add(item.id); page.items.push(item); }
          if (!replacement && (!this.remoteItem.id || data.item.name !== 'QQ音乐艺人')) this.remoteItem = { ...this.remoteItem, ...data.item };
          if (data.total != null && Number.isFinite(Number(data.total))) page.total = Math.max(page.loaded ? page.total ?? 0 : 0, Number(data.total));
          page.scope = data.scope === 'popular' ? 'popular' : 'all';
          page.partial = page.partial || Boolean(data.partial); page.nextCursor = next; page.loaded = true; page.cursors.push(offset);
          if (!replacement && section === 'songs' && this.pages.albums.total === null && data.albumTotal != null) this.pages.albums.total = Number(data.albumTotal);
          // Older catalogue fixtures bundle albums with their unsectioned response.
          if (!replacement && !data.section && section === 'songs' && Array.isArray(data.albums)) { this.pages.albums.items = data.albums; this.pages.albums.loaded = true; }
          return true;
        } catch (error) {
          if (current()) page.error = error.message || '作品载入失败';
          return false;
        } finally {
          if (current()) { page.loading = false; page.pending = null; if (!replacement) this.$nextTick(this.bindPrefetch); }
        }
      })();
      return page.pending;
    },
    collectSongs(restart = false) {
      if (this.isLocal) return Promise.resolve([...this.tracks]);
      if (this._collectPromise) return this._collectPromise;
      // Reuse the same paginator without replacing visible rows before every page succeeds.
      const ticket = this.requestId, owner = this.pages.songs, page = restart ? sectionState() : owner;
      const ownerToken = restart ? ++owner.token : owner.token;
      const current = () => ticket === this.requestId && this.pages.songs === owner && this._collectPromise === operation && (!restart || ownerToken === owner.token);
      this.collecting = true;
      if (restart) { owner.loading = true; owner.error = ''; }
      const operation = (async () => {
        do {
          const ok = await this.loadPage('songs', true, restart ? page : null);
          if (!current() || (!ok && !page.error)) throw Object.assign(new Error('歌曲收集已取消'), { name: 'AbortError' });
          if (!ok) throw new Error(page.error);
        } while (page.nextCursor !== null);
        if (page.scope !== 'all' || page.partial || (page.total !== null && page.items.length < page.total)) throw new Error('部分歌曲暂时无法读取，请重试后再播放或保存全部');
        if (restart) {
          for (const key of ['items', 'total', 'nextCursor', 'loaded', 'scope', 'partial', 'cursors']) owner[key] = page[key];
          owner.error = '';
        }
        return [...page.items];
      })().catch(error => { if (restart && current() && error.name !== 'AbortError') owner.error = error.message || '歌曲重读失败'; throw error; }).finally(() => {
        if (ticket === this.requestId && this._collectPromise === operation) {
          if (restart && current()) { owner.loading = false; owner.pending = null; }
          this.collecting = false; this._collectPromise = null;
          this.bindPrefetch();
        }
      });
      this._collectPromise = operation;
      if (restart) owner.pending = operation;
      return operation;
    },
    async playAll() {
      const ticket = this.requestId, playbackTicket = runtime.reservePlaybackRequest();
      try {
        const tracks = await this.collectSongs();
        if (ticket === this.requestId && runtime.isPlaybackRequestCurrent(playbackTicket) && tracks.length) await runtime.playTracks(tracks);
      } catch (error) { if (ticket === this.requestId && runtime.isPlaybackRequestCurrent(playbackTicket) && error.name !== 'AbortError') runtime.notify(error.message || '播放失败', 'error'); }
    },
    async saveArtist() {
      const ticket = this.requestId, name = this.artist.name;
      try {
        const tracks = await this.collectSongs();
        if (ticket !== this.requestId || !tracks.length) return;
        await runtime.createPlaylist(name, tracks); runtime.notify('已保存为歌单');
      } catch (error) { if (ticket === this.requestId && error.name !== 'AbortError') runtime.notify(error.message || '保存失败', 'error'); }
    },
  },
};
</script>
<style lang="scss" scoped>
.page-state { color: var(--color-text); opacity: .78; line-height: 1.8; padding: 36px 0; }
.artist-page .artist-info .page-state.artist-song-retry { padding:12px 0 0; }
.artist-info .description.expanded { -webkit-line-clamp: unset; white-space: pre-line; }
.artist-info .buttons { gap: 12px; flex-wrap: wrap; }
.artist-info img { object-fit: cover; background: var(--color-secondary-bg); }
button:disabled { opacity: .4; cursor: default; }
@media(max-width: 800px) { .artist-info { align-items: flex-start; flex-direction: column; } .artist-info img { width: 192px; height: 192px; margin: 0 0 24px; } .artist-info .name { font-size: 36px; overflow-wrap: anywhere; } .artist-info .artist { margin-top: 12px; } }

.artist-page {
  margin-top: 32px;
}

.artist-info {
  display: flex;
  align-items: center;
  margin-bottom: 26px;
  color: var(--color-text);
  img {
    height: 248px;
    width: 248px;
    border-radius: 50%;
    margin-right: 56px;
    box-shadow: rgba(0, 0, 0, 0.2) 0px 12px 16px -8px;
  }
  .name {
    font-size: 56px;
    font-weight: 700;
  }

  .artist {
    font-size: 18px;
    opacity: 0.88;
    margin-top: 24px;
  }

  .statistics {
    font-size: 14px;
    opacity: 0.68;
    margin-top: 2px;
  }

  .buttons {
    margin-top: 26px;
    display: flex;
    .shuffle {
      padding: 8px 11px;
      .svg-icon {
        margin: 0;
      }
    }
  }

  .description {
    user-select: none;
    font-size: 14px;
    opacity: 0.68;
    margin-top: 24px;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    cursor: pointer;
    white-space: pre-line;
    &:hover {
      transition: opacity 0.3s;
      opacity: 0.88;
    }
  }
}

.section-title {
  font-weight: 600;
  font-size: 22px;
  opacity: 0.88;
  color: var(--color-text);
  margin-bottom: 16px;
  padding-top: 46px;

  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  a {
    font-size: 13px;
    font-weight: 600;
    opacity: 0.68;
  }
}

.artist-sections { display:flex; gap:8px; align-items:center; margin-top:16px; }
.artist-page:not(.salt-detail) .artist-sections > button { position:relative; padding:8px 16px; border:0; border-radius:999px; background:transparent; color:inherit; font:inherit; cursor:pointer; }
.artist-page:not(.salt-detail) .artist-sections > button.selected { background:var(--color-secondary-bg); }
.loaded-count { margin-left:12px; font-size:12px; font-weight:400; opacity:.7; }
.artist-page-tail { min-height:1px; }

.similar-artists {
  .section-title {
    margin-bottom: 24px;
  }
}

.latest-mv {
  .cover {
    position: relative;
    transition: transform 0.3s;
    &:hover {
      cursor: pointer;
    }
  }
  img {
    border-radius: 0.75em;
    height: 128px;
    object-fit: cover;
    user-select: none;
  }

  .shadow {
    position: absolute;
    top: 6px;
    height: 100%;
    width: 100%;
    filter: blur(16px) opacity(0.4);
    transform: scale(0.9, 0.9);
    z-index: -1;
    background-size: cover;
    border-radius: 0.75em;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }
}

.description-fulltext {
  font-size: 16px;
  margin-top: 24px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-line;
}

</style>

<style lang="scss" scoped>
/* SaltUI controls and compact detail header, retaining the artist sections. */
.artist-page.salt-detail {
  margin-top:16px; color:var(--salt-text); font-size:var(--salt-text-main,14px);
  .artist-info { flex-direction:row; align-items:flex-start; gap:16px; margin-bottom:24px; }
  .artist-info > .head { flex:0 0 160px; }
  .artist-info > div:last-child { flex:1; min-width:0; }
  .artist-info img { display:block; width:160px; height:160px; margin:0; box-shadow:none; }
  .artist-info .name { font-size:var(--salt-text-title,24px); line-height:1.35; font-weight:500; overflow-wrap:anywhere; }
  .artist-info .artist { margin-top:8px; color:var(--salt-subtext); font-size:14px; line-height:1.5; opacity:1; }
  .artist-info .statistics { margin-top:2px; color:var(--salt-subtext); font-size:12px; line-height:1.5; opacity:1; }
  .artist-info .description { margin-top:12px; color:var(--salt-subtext); font-size:14px; line-height:1.5; opacity:1; overflow-wrap:anywhere; }
  .artist-info .buttons { margin-top:16px; gap:8px; }
  .salt-ui-button:disabled { opacity:1; }
  .section-title { padding-top:24px; margin-bottom:12px; color:var(--salt-text); font-size:16px; font-weight:500; line-height:1.5; opacity:1; }
  .artist-sections > button { position:relative; }
  .artist-sections > button.selected { background:var(--salt-hover-overlay); color:var(--salt-accent); }
  .artist-sections > button.selected::before { content:""; position:absolute; bottom:2px; left:calc(50% - 6px); width:12px; height:3px; border-radius:999px; background:var(--salt-accent); }
  .page-state { padding:24px 0; color:var(--salt-subtext); font-size:14px; opacity:1; }
  ::v-deep .cover img { display:block; box-sizing:border-box; border-radius:6px; border-color:var(--salt-stroke); }
  ::v-deep .cover .shadow { display:none; }
  ::v-deep .cover .play-button { position:relative; width:32px !important; height:32px !important; padding:0; border:0; border-radius:9999px; background:var(--salt-accent); color:var(--salt-on-accent); backdrop-filter:none; box-shadow:none; transform:none; transition:none; overflow:hidden; isolation:isolate; }
  ::v-deep .cover .play-button::after { content:''; position:absolute; inset:0; pointer-events:none; }
  ::v-deep .cover .play-button:not(:disabled):hover::after,::v-deep .cover .play-button:not(:disabled):focus-visible::after { background:var(--salt-hover-overlay); }
  ::v-deep .cover .play-button:not(:disabled):active::after { background:var(--salt-press-overlay); }
  ::v-deep .cover .play-button .svg-icon { width:16px; height:16px; margin:0; }
  ::v-deep .cover-row .item .text .title { font-weight:400; line-height:1.5; }
  ::v-deep .cover-row .item .text .info { color:var(--salt-subtext); opacity:1; }
}

#app.salt-mobile-layout .artist-page.salt-detail {
  --detail-mobile-cover-size:144px;
  margin-top:12px;
  .artist-info { flex-direction:column; align-items:stretch; gap:16px; margin:0 0 20px; }
  .artist-info > .head { align-self:center; flex:0 0 auto; width:var(--detail-mobile-cover-size); height:var(--detail-mobile-cover-size); }
  .artist-info .head img { width:100%; height:100%; object-fit:cover; }
  .artist-info > div:last-child { flex:none; width:100%; }
  .artist-info .name { font-size:24px; line-height:1.3; overflow-wrap:anywhere; }
  .artist-info .artist,.artist-info .statistics { overflow-wrap:anywhere; }
  .artist-info .buttons { margin-top:12px; gap:8px; }
  .salt-ui-button { min-width:44px; min-height:44px; max-width:100%; }
  .buttons ::v-deep .label { white-space:normal; overflow-wrap:anywhere; }
  .artist-sections { margin-top:0; }
  .section-title { flex-wrap:wrap; justify-content:flex-start; gap:4px 12px; padding-top:16px; }
  .loaded-count { margin-left:0; }
  ::v-deep .track-list-items { grid-template-columns:minmax(0,1fr) !important; }
  @media (orientation:landscape) and (max-height:599px) {
    --detail-mobile-cover-size:96px;
    .artist-info { flex-direction:row; align-items:flex-start; gap:16px; margin-bottom:12px; }
    .artist-info > .head { align-self:flex-start; flex-basis:var(--detail-mobile-cover-size); }
    .artist-info > div:last-child { flex:1; width:auto; }
    .artist-info .name { font-size:22px; }
    .artist-info .artist { margin-top:4px; }
    .artist-info .buttons { margin-top:8px; }
  }
}

@media (min-width:600px) and (max-width:1023px) and (max-height:599px) {
  #app.salt-shell .artist-page.salt-detail {
    .artist-info { flex-direction:row; align-items:flex-start; gap:16px; margin-bottom:12px; }
    .artist-info > .head { align-self:flex-start; flex:0 0 96px; width:96px; height:96px; }
    .artist-info > div:last-child { flex:1; width:auto; }
    .artist-info .name { font-size:22px; }
    .artist-info .artist { margin-top:4px; }
    .artist-info .buttons { margin-top:8px; }
    .salt-ui-button { min-width:44px; min-height:44px; max-width:100%; }
    ::v-deep .track-list-items { grid-template-columns:minmax(0,1fr) !important; }
  }
}
</style>

<style lang="scss" scoped>
/* Phones retain one action rail. Secondary commands collapse to named icons,
   while the primary Play command and desktop labels remain readable. */
.detail-action-icon,.detail-busy-label { display:none; }
@media (max-width:599px), (max-width:1023px) and (max-height:599px) and (pointer:coarse) {
  .artist-page {
    .artist-info .buttons.detail-actions { display:flex; flex-wrap:nowrap; gap:6px; min-width:0; width:100%; }
    .buttons.detail-actions > button { flex:none; min-width:44px; min-height:44px; height:44px; margin:0; padding-inline:12px !important; font-size:14px; white-space:nowrap; }
    .buttons.detail-actions > button.detail-secondary { flex:0 0 44px; width:44px; padding:0 !important; }
    .detail-secondary .detail-action-label { display:none; }
    .detail-action-icon { display:block; flex:none; width:20px; height:20px; margin:0 !important; }
    .buttons.detail-actions ::v-deep .label { white-space:nowrap; }
    .buttons.detail-actions > button:not(.detail-secondary) .detail-action-label { display:none; }
    .detail-busy-label { display:inline; }
  }
}
@media(max-width:599px) {
  .artist-page:not(.salt-detail) {
    .artist-info { width:100%; flex-direction:column; align-items:stretch; gap:16px; }
    .artist-info > div:last-child { flex:none; width:100%; min-width:0; margin:0; }
    .artist-info > .head { align-self:center; }
    .artist-info .head img { width:144px; height:144px; object-fit:cover; }
  }
}
</style>
